import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Scissors, Plus, Pencil, Trash2, RefreshCw, X, Upload } from 'lucide-react';

function api(method, url, data = null, config = {}) {
  const pw = sessionStorage.getItem('admin_password');
  return axios({ method, url, data, ...config, headers: { 'x-admin-password': pw, ...config.headers } });
}

// Lê o arquivo, redimensiona (máx 800px) e devolve um data URL JPEG.
// Enviar como base64 evita a corrupção de upload binário no Netlify.
function fileToResizedDataUrl(file, maxDim = 800, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = Math.round((height * maxDim) / width);
        width = maxDim;
      } else if (height > maxDim) {
        width = Math.round((width * maxDim) / height);
        height = maxDim;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível ler a imagem.')); };
    img.src = url;
  });
}

const colors = [
  'linear-gradient(135deg,#c9a96e,#a07840)',
  'linear-gradient(135deg,#9b59b6,#7d3c98)',
  'linear-gradient(135deg,#2d8c4e,#1e6b38)',
  'linear-gradient(135deg,#2980b9,#1a5276)',
  'linear-gradient(135deg,#e74c3c,#c0392b)',
];

const initials = (name) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

function StylistModal({ stylist, onClose, onSaved }) {
  const [name, setName] = useState(stylist?.name || '');
  const [specialty, setSpecialty] = useState(stylist?.specialty || '');
  const [bio, setBio] = useState(stylist?.bio || '');
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(
    stylist?.photo ? `/uploads/${stylist.photo}` : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !specialty.trim()) {
      setError('Nome e especialidade são obrigatórios.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let photo_base64 = null;
      if (photo) photo_base64 = await fileToResizedDataUrl(photo);

      const body = { name, specialty, bio, photo_base64 };
      const pw = sessionStorage.getItem('admin_password');
      const headers = { 'x-admin-password': pw };
      if (stylist) {
        await axios.put(`/api/admin/stylists/${stylist.id}`, body, { headers });
      } else {
        await axios.post('/api/admin/stylists', body, { headers });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>{stylist ? 'Editar Profissional' : 'Nova Profissional'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-gray)' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Photo upload */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            {preview ? (
              <img src={preview} alt="preview" className="photo-preview" style={{ width: 72, height: 72 }} />
            ) : (
              <div className="photo-initials" style={{ background: colors[0], width: 72, height: 72, fontSize: '1.3rem' }}>
                {name ? initials(name) : '?'}
              </div>
            )}
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              cursor: 'pointer', color: 'var(--gold-dark)', fontSize: '0.85rem',
              fontWeight: 500, border: '1.5px dashed var(--gold-light)',
              borderRadius: '10px', padding: '0.6rem 1rem',
            }}>
              <Upload size={16} />
              {photo ? photo.name.slice(0, 20) + '...' : 'Escolher foto'}
              <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
            </label>
          </div>

          <label>
            Nome completo *
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Patricia Pourroy" required />
          </label>

          <label>
            Especialidade *
            <input value={specialty} onChange={e => setSpecialty(e.target.value)} placeholder="Ex: Coloração & Tratamentos" required />
          </label>

          <label>
            Bio / Descrição
            <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Breve descrição da profissional..." />
          </label>

          {error && (
            <div style={{
              background: '#fff0f0', border: '1px solid #ffd5d5', color: '#cc3333',
              padding: '0.65rem 1rem', borderRadius: '10px', fontSize: '0.82rem',
            }}>
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1 }}>
              {loading ? 'Salvando...' : stylist ? 'Salvar Alterações' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminStylists() {
  const [stylists, setStylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | stylist object

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/stylists')
      .then(r => setStylists(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`Remover ${name}? Esta ação não pode ser desfeita.`)) return;
    await api('delete', `/api/admin/stylists/${id}`);
    load();
  };

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 className="admin-section-title" style={{ marginBottom: 0 }}>
          Profissionais <span style={{ fontSize: '1rem', color: 'var(--text-gray)', fontFamily: 'Poppins' }}>({stylists.length})</span>
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-secondary" style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={load}>
            <RefreshCw size={15} /> Atualizar
          </button>
          <button className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={() => setModal('new')}>
            <Plus size={16} /> Nova Profissional
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : stylists.length === 0 ? (
          <div className="empty-state">
            <Scissors size={40} strokeWidth={1} style={{ color: 'var(--gold-light)', marginBottom: '0.75rem' }} />
            <p>Nenhuma profissional cadastrada.</p>
            <br />
            <button className="btn-primary" onClick={() => setModal('new')}>
              <Plus size={15} /> Cadastrar Profissional
            </button>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Nome</th>
                <th>Especialidade</th>
                <th>Bio</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {stylists.map((s, idx) => (
                <tr key={s.id}>
                  <td>
                    {s.photo ? (
                      <img
                        src={`/uploads/${s.photo}`}
                        alt={s.name}
                        className="photo-preview"
                        onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                      />
                    ) : null}
                    <div
                      className="photo-initials"
                      style={{ background: colors[idx % colors.length], width: 48, height: 48, fontSize: '0.95rem', display: s.photo ? 'none' : 'flex' }}
                    >
                      {initials(s.name)}
                    </div>
                  </td>
                  <td><strong>{s.name}</strong></td>
                  <td>
                    <span style={{
                      background: 'rgba(201,169,110,0.1)', color: 'var(--gold-dark)',
                      padding: '0.2rem 0.7rem', borderRadius: '50px', fontSize: '0.78rem', fontWeight: 500,
                    }}>
                      {s.specialty}
                    </span>
                  </td>
                  <td style={{ maxWidth: '200px', color: 'var(--text-gray)', fontSize: '0.83rem' }}>
                    <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {s.bio || '—'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn-icon edit" title="Editar" onClick={() => setModal(s)}>
                        <Pencil size={15} />
                      </button>
                      <button className="btn-icon" title="Remover" onClick={() => remove(s.id, s.name)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <StylistModal
          stylist={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
